import React, { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { Download } from "lucide-react";
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip } from "recharts";
import { classNames } from "../../lib/utils";
import { StatChip, downloadCSV } from "./_shared";

function CategoryHighlightCard({ tone, title, item, fallback, testId, currentYear, previousYear }) {
  const tones = {
    emerald: { card: "border-emerald-200 bg-emerald-50/40", chip: "bg-emerald-100 text-emerald-700" },
    amber:   { card: "border-amber-200 bg-amber-50/40",   chip: "bg-amber-100 text-amber-700" },
  }[tone];
  return (
    <div className={classNames("nv-card p-5 border-2", tones.card)} data-testid={testId}>
      <p className="text-small-caps text-slate-600">{title}</p>
      {item ? (
        <>
          <p className="text-h3 mt-1 font-semibold text-slate-900">{item.category}</p>
          <div className="mt-3 flex items-baseline gap-3">
            <span className="text-3xl font-heading font-bold tabular-nums">{item.current}%</span>
            <span className="text-sm text-slate-500 tabular-nums">vs {item.previous}% last year</span>
          </div>
          <div className="mt-2">
            <span className={classNames("inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-semibold", tones.chip)}>
              {item.delta > 0 ? "▲" : "▼"} {item.delta > 0 ? "+" : ""}{item.delta} pts {currentYear} vs {previousYear}
            </span>
          </div>
        </>
      ) : (
        <p className="text-sm text-slate-500 mt-2">{fallback}</p>
      )}
    </div>
  );
}

export default function QAYoYReport({ home, year }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
    const params = { year };
    if (home) params.home = home;
    api.get("/qa/yoy-trend", { params })
      .then(({ data }) => setData(data))
      .catch((e) => toast.error(e?.response?.data?.detail || "Failed to load YoY trend"))
      .finally(() => setLoading(false));
  }, [home, year]);

  const exportCSV = () => {
    if (!data) return;
    const rows = data.trend.map((t) => ({
      month: t.month_label,
      [`${data.current_year}_score_pct`]: t.current ?? "",
      [`${data.previous_year}_score_pct`]: t.previous ?? "",
    }));
    downloadCSV(`nv-qa-yoy-${data.home}-${data.current_year}-vs-${data.previous_year}.csv`, rows);
    toast.success("CSV downloaded");
  };

  if (loading || !data) {
    return <div className="nv-card p-8 text-center text-sm text-slate-500" data-testid="yoy-loading">Loading year-over-year trend…</div>;
  }

  const { trend, ytd, current_year, previous_year, strongest_improving,
          weakest_declining, category_deltas, data_points } = data;
  const noData = data_points.current_year === 0 && data_points.previous_year === 0;
  const ytdDelta = ytd.delta;
  const ytdTone = ytdDelta == null ? "slate" : ytdDelta >= 0 ? "emerald" : "amber";
  const ytdSign = ytdDelta == null ? "" : ytdDelta >= 0 ? "+" : "";

  return (
    <div className="space-y-5" data-testid="yoy-report">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="text-small-caps text-slate-500">QA Performance · Year over Year</p>
          <h2 className="text-h2 font-semibold mt-1">
            {data.home === "ALL" ? "All Homes" : data.home} · {current_year} vs {previous_year}
          </h2>
          <p className="text-xs text-slate-500 mt-1">
            Are we improving over time? {ytdDelta == null
              ? <span className="text-slate-500">Need data from both years to compare YTD.</span>
              : ytdDelta > 0
                ? <span className="text-emerald-700 font-medium">YTD is up {ytdDelta} pts over the same {ytd.months_compared} month{ytd.months_compared === 1 ? "" : "s"} last year.</span>
                : ytdDelta < 0
                  ? <span className="text-amber-700 font-medium">YTD is down {Math.abs(ytdDelta)} pts compared to the same {ytd.months_compared} month{ytd.months_compared === 1 ? "" : "s"} last year.</span>
                  : <span className="text-slate-700 font-medium">YTD is flat vs same period last year.</span>}
          </p>
        </div>
        <button onClick={exportCSV} disabled={noData} className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-sky-800 hover:bg-sky-900 text-white text-sm font-semibold disabled:opacity-50" data-testid="export-yoy-csv">
          <Download size={14} /> Export CSV
        </button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" data-testid="yoy-stat-row">
        <StatChip label={`${current_year} YTD`} value={ytd.current != null ? `${ytd.current}%` : "—"} tone="sky" />
        <StatChip label={`${previous_year} (same months)`} value={ytd.previous != null ? `${ytd.previous}%` : "—"} tone="slate" />
        <StatChip label="YoY Delta" value={ytdDelta != null ? `${ytdSign}${ytdDelta} pts` : "—"} tone={ytdTone} />
      </div>

      <div className="nv-card p-5">
        <h3 className="text-h3">Monthly Overall QA Score</h3>
        <p className="text-xs text-slate-500 mt-1">{current_year} (sky blue) overlaid on {previous_year} (gray). Months with no audit are gaps.</p>
        <div className="mt-4 h-72 min-w-0" data-testid="yoy-chart">
          <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={0} debounce={50}>
            <LineChart data={trend} margin={{ top: 10, right: 16, left: -10, bottom: 0 }}>
              <XAxis dataKey="month_label" tick={{ fontSize: 12 }} />
              <YAxis domain={[0, 100]} tick={{ fontSize: 12 }} />
              <Tooltip
                formatter={(v, n) => [v == null ? "—" : `${v}%`, n === "current" ? `${current_year}` : `${previous_year}`]}
                contentStyle={{ fontSize: 12, borderRadius: 8 }}
              />
              <Line type="monotone" dataKey="previous" name={`${previous_year}`} stroke="#94a3b8" strokeWidth={2} strokeDasharray="6 4" dot={{ r: 3, fill: "#94a3b8" }} connectNulls />
              <Line type="monotone" dataKey="current" name={`${current_year}`} stroke="#0369a1" strokeWidth={2.5} dot={{ r: 4, fill: "#0369a1" }} connectNulls />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <CategoryHighlightCard
          tone="emerald"
          title="Strongest improving category"
          item={strongest_improving}
          fallback="No category is up YoY yet for this scope."
          testId="yoy-strongest-improving"
          currentYear={current_year}
          previousYear={previous_year}
        />
        <CategoryHighlightCard
          tone="amber"
          title="Weakest declining category"
          item={weakest_declining}
          fallback="No category is down YoY for this scope. 🎉"
          testId="yoy-weakest-declining"
          currentYear={current_year}
          previousYear={previous_year}
        />
      </div>

      {category_deltas.length > 0 && (
        <div className="nv-card overflow-x-auto">
          <table className="w-full text-sm" data-testid="yoy-category-table">
            <thead className="bg-slate-50 text-slate-600 text-xs uppercase tracking-wider">
              <tr>
                <th className="px-5 py-3 text-left">Category</th>
                <th className="px-3 py-3 text-right">{previous_year}</th>
                <th className="px-3 py-3 text-right">{current_year}</th>
                <th className="px-3 py-3 text-right">Δ</th>
              </tr>
            </thead>
            <tbody>
              {category_deltas.map((c) => (
                <tr key={c.category} className="border-t border-slate-100">
                  <td className="px-5 py-3 font-medium text-slate-800">{c.category}</td>
                  <td className="px-3 py-3 text-right tabular-nums text-slate-500">{c.previous}%</td>
                  <td className="px-3 py-3 text-right tabular-nums font-bold">{c.current}%</td>
                  <td className={classNames("px-3 py-3 text-right tabular-nums font-semibold",
                    c.delta > 0 ? "text-emerald-700" : c.delta < 0 ? "text-amber-700" : "text-slate-500")}>
                    {c.delta > 0 ? "+" : ""}{c.delta}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {noData && (
        <div className="nv-card p-8 text-center text-sm text-slate-500" data-testid="yoy-empty">
          No QA audit data exists for either {current_year} or {previous_year} in this scope.
        </div>
      )}
    </div>
  );
}
